// services/JwtAuthService.js
import jwt_decode from "jwt-decode";
import localStorageService from "./localStorageService";
import { resetQueryCache } from "../ui/data/queryClient";

// Sliding session timeout: 8 hours of inactivity (a working day of watching
// tests, feeds or the portfolio never signs you out). App.js counts mouse
// movement, keys, clicks, scrolls and a visible tab as activity.
export const SESSION_TIMEOUT = 8 * 60 * 60 * 1000;

// While the user is active, a token with less than this left is renewed
// (POST /api/auth/refresh), so an active session is not cut off at the
// token's 24 h expiry. The api still ends a session 30 days after sign-in.
export const RENEW_WHEN_LEFT_MS = 12 * 60 * 60 * 1000;

// Grace period to account for client clock skew
const CLOCK_SKEW_MS = 30 * 1000;

// Session bookkeeping only. The network calls (login, register, verify,
// forgot/change password) live in containers/Login/UserFunctions.js,
// SignInForm, services/passwordResetService.js and services/accountService.js
// on the shared http instance; the old fetch-based helpers here (and src/api.js
// they depended on) were unreachable and have been removed.
class JwtAuthService {
  // Check if the JWT itself is expired (server-issued exp claim)
  isTokenExpired = () => {
    const token = localStorage.getItem("token");
    if (!token) return true;
    try {
      const decoded = jwt_decode(token);
      if (!decoded.exp) return false;
      return decoded.exp * 1000 < Date.now() - CLOCK_SKEW_MS;
    } catch (e) {
      return true;
    }
  };

  // Check if the inactivity-based session is still valid
  isSessionActive = () => {
    const sessionStart = localStorage.getItem("session_start");
    if (!sessionStart) return false;
    const elapsed = Date.now() - parseInt(sessionStart, 10);
    return elapsed < SESSION_TIMEOUT;
  };

  // Combined check: must have a valid JWT AND be within the inactivity window
  isSessionValid = () => {
    return !this.isTokenExpired() && this.isSessionActive();
  };

  getSessionTimeRemaining = () => {
    const sessionStart = localStorage.getItem("session_start");
    if (!sessionStart) return 0;
    const elapsed = Date.now() - parseInt(sessionStart, 10);
    const remaining = SESSION_TIMEOUT - elapsed;
    return remaining > 0 ? remaining : 0;
  };

  // Refresh inactivity timer on user activity (debounced from App.js side)
  refreshSession = () => {
    if (!this.isSessionValid()) return;
    localStorage.setItem("session_start", Date.now().toString());
    this.startSessionTimer();
    this.renewTokenIfDue();
  };

  // Ms until the stored token expires (0 when missing / unreadable).
  tokenTimeLeft = () => {
    const token = localStorage.getItem("token");
    if (!token) return 0;
    try {
      const decoded = jwt_decode(token);
      return decoded.exp ? decoded.exp * 1000 - Date.now() : Number.POSITIVE_INFINITY;
    } catch (e) {
      return 0;
    }
  };

  // Single-flight renewal, only from user activity (refreshSession). The new
  // token replaces the old one in place — same user, so the cached reads stay
  // (setSession would reset them). A refusal (e.g. the 30-day cap) is left to
  // the token's own expiry and the 401 handling; nothing is torn down here.
  renewTokenIfDue = () => {
    if (this.renewing) return this.renewing;
    const left = this.tokenTimeLeft();
    if (!(left > 0) || left > RENEW_WHEN_LEFT_MS) return null;
    // Lazy: services/http imports this module (401 → handleSessionExpired).
    // eslint-disable-next-line global-require
    const http = require("./http").default;
    const before = localStorage.getItem("token");
    this.renewing = http.post("/api/auth/refresh")
      .then((res) => {
        const token = res && res.data && res.data.token;
        // Only if no sign-out / new sign-in happened meanwhile.
        if (token && localStorage.getItem("token") === before) {
          localStorage.setItem("token", token);
          this.startSessionTimer();
        }
      })
      .catch(() => { /* keep the current token until it expires */ })
      .finally(() => { this.renewing = null; });
    return this.renewing;
  };

  startSessionTimer = () => {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
    }

    // Schedule a logout at the earlier of: inactivity timeout OR JWT expiry
    const inactivityRemaining = this.getSessionTimeRemaining();

    let jwtRemaining = Number.POSITIVE_INFINITY;
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwt_decode(token);
        if (decoded.exp) {
          jwtRemaining = decoded.exp * 1000 - Date.now();
        }
      } catch (e) {
        jwtRemaining = 0;
      }
    }

    const fireIn = Math.max(0, Math.min(inactivityRemaining, jwtRemaining));

    this.sessionTimer = setTimeout(() => {
      console.log('[Session] Timeout - logging out');
      this.handleSessionExpired();
    }, fireIn);
  };

  // Centralized expiry handler — emits an event so SessionWatcher can redirect
  handleSessionExpired = () => {
    this.logout();
    // Notify the app (SessionWatcher listens for this)
    window.dispatchEvent(new Event('session-expired'));
  };

  loginWithToken = () => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem("token");
      const user = localStorageService.getItem("auth_user");

      if (!token || !user) {
        reject(new Error("No session found"));
        return;
      }

      if (!this.isSessionValid()) {
        this.logout();
        reject(new Error("Session expired"));
        return;
      }

      this.setSession(token);
      this.startSessionTimer();
      resolve(user);
    });
  };

  logout = () => {
    if (this.sessionTimer) {
      clearTimeout(this.sessionTimer);
      this.sessionTimer = null;
    }
    this.setSession(null);
    this.removeUser();
  };

  // Unified key naming: 'token' (matches App.js) and 'authenticated'.
  // The token is attached per-request by services/http.js — never via
  // axios.defaults, which would send it to every third-party host too.
  setSession = (token) => {
    // A new session or none: the cached reads (ui/data) belong to whoever was
    // signed in before, so they go. Covers login, logout(), handleSessionExpired
    // (the 401 interceptor, the session timer, SessionWatcher) and AuthRoute.
    if (!token || token !== localStorage.getItem("token")) resetQueryCache();
    if (token) {
      localStorage.setItem("token", token);
      localStorage.setItem("authenticated", "true");
      localStorage.setItem("session_start", Date.now().toString());
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("authenticated");
      localStorage.removeItem("session_start");
      localStorage.removeItem("userId");
      localStorage.removeItem("email");
    }
  };

  setUser = (user) => {
    localStorageService.setItem("auth_user", user);
  };

  removeUser = () => {
    localStorage.removeItem("auth_user");
  };
}

export default new JwtAuthService();